// Shared constants for AI Dispatcher module

export const DEPARTMENTS = [
  'Microcorp Quotes',
  'Commissions',
  'Cable Support',
  'Elite Commissions',
  'Quotes',
  'Orders',
  'Cloud and SaaS',
  'Account Management Support',
  'Revenue Assurance',
  'Training',
  'Mobility',
  'Omni Center',
  'Invest orders',
  'ADCom Orders',
  'Specialized Teams',
];

// Mailbox filter choices, shared by the Decisions and Analytics tabs. The
// empty value means "no distro filter".
export const DISTRO_OPTIONS = [
  { value: '', label: 'All distros' },
  { value: 'quotes', label: 'quotes@' },
  { value: 'orders', label: 'orders@' },
  { value: 'cable', label: 'cable@' },
  { value: 'oni', label: 'ONI' },
  { value: 'invest', label: 'invest@' },
];

// Ticket Type enum. Mirrors TICKET_TYPES in backend/routes/ai_dispatcher.py —
// same values, same order.
//
// The backend also defines TICKET_TYPE_DO_NOT_USE = "New Order (DO NOT USE)",
// which it normalizes to "New Order" before persisting. It is deliberately
// absent from this list: a human labeller must not be able to pick a value the
// backend would silently rewrite, because the rewritten value would then be
// scored against the AI's answer as though the human had chosen it.
export const TICKET_TYPES = [
  'Billing Issue',
  'Other',
  'New Order',
  'Trouble / Outage',
  'MACD',
  'Elite Orders',
  'Order',
  // Added 2026-09-08 — see the matching note in backend/routes/ai_dispatcher.py.
  // `Quote` (singular) is the ticket type; `Quotes` (plural) is the department.
  'Quote',
  'Appsmart ASO Order',
  'Reports',
  'Invest - New Order',
  'OC - New Order',
];

// Accepted as an input label and rewritten to "New Order" before storage. Not
// offered in any picker, but recognised here so a pasted value is not marked
// off-enum for a rewrite the backend is about to perform anyway.
export const TICKET_TYPE_DO_NOT_USE = 'New Order (DO NOT USE)';

/**
 * Fold a taxonomy label to the form used for EQUALITY ONLY — never for
 * storage. Mirrors `_comparison_key` in backend/routes/ai_dispatcher.py:
 * remove every whitespace character, then case-fold.
 *
 * Whitespace is removed outright rather than trimmed, because both traps in
 * AppDirect's live data need it: `'Cloud and SaaS '` (trailing space) and
 * `Trouble/Outage` vs `Trouble / Outage` (interior spacing).
 */
export function comparisonKey(value) {
  if (value == null) return null;
  return String(value).replace(/\s+/g, '').toLowerCase();
}

/**
 * True when a human label names something our classifier cannot emit.
 *
 * ~68% of AppDirect's real ticket types and 7.2% of their departments fall
 * outside our enums (`CNSG Orders`, `Quote`, `Cable Support Request`, …) — see
 * docs/dispatch/APPDIRECT_TAXONOMY_OBSERVED.md §2-3. Those labels are correct
 * ground truth, not input errors; the backend stores them verbatim and flags
 * them. This mirrors that check so the UI can say so at the point of entry
 * rather than waiting for the round trip.
 *
 * The server is authoritative — this is a display hint, and the flag on the
 * saved decision (`human_value_off_enum`) is what anything downstream reads.
 */
export function isOffEnum(value, options) {
  if (value == null || String(value).trim() === '') return false;
  let key = comparisonKey(value);
  if (key === comparisonKey(TICKET_TYPE_DO_NOT_USE)) {
    key = comparisonKey('New Order');
  }
  return !options.some((o) => comparisonKey(o) === key);
}

/**
 * The six departments Dispatch can actually route a ticket to.
 *
 * Chris Hollock, 2026-07-30 call @25:44: "Between what we work on from our side
 * is cable support, quotes, Omnicenter, Invest orders, specialized team and just
 * general orders. Those are the only ones that my Dispatch team works on."
 *
 * Mirrors ROUTABLE_DEPARTMENTS in the n8n workflow's `Resolve Dept and Type`
 * node. A model answer outside this set is mapped to Orders and flagged
 * NON_ROUTABLE_DEPARTMENT, so a team here that is NOT in this list can never
 * receive a ticket however it is configured.
 */
export const ROUTABLE_DEPARTMENTS = [
  'Orders',
  'Cable Support',
  'Quotes',
  'Omni Center',
  'Invest orders',
  'Specialized Teams',
];

/**
 * Real teams that take work, but by hand-off rather than by dispatch.
 * OrdersDispatchAssignment §19 forwards Mobility mail to mobility@appdirect.com
 * — a forward, not a queue assignment — so these are held for a human rather
 * than routed or defaulted to Orders (flag FORWARD_ONLY_DEPARTMENT).
 */
export const FORWARD_ONLY_DEPARTMENTS = ['Mobility'];

/**
 * Classify a department for display: 'routable' | 'forward_only' | 'not_a_destination'.
 *
 * Compared through `comparisonKey` on purpose. The seeded department is
 * `Invest orders` (lower-case o) while its team is named `Invest Orders`; an
 * exact-string check would silently reclassify it the moment somebody tidied
 * that capitalisation in the UI.
 */
export function routingRole(department) {
  const key = comparisonKey(department);
  if (key == null) return 'not_a_destination';
  if (ROUTABLE_DEPARTMENTS.some((d) => comparisonKey(d) === key)) return 'routable';
  if (FORWARD_ONLY_DEPARTMENTS.some((d) => comparisonKey(d) === key)) return 'forward_only';
  return 'not_a_destination';
}

// Names the assignment mode as a *setting*, for the Teams screens where an
// admin is choosing one. The reviewer-facing phrasing of the same values —
// "next in the team's rotation" — lives in OWNER_MODE_METHOD_LABELS below,
// because a sentence explaining a past decision and a dropdown option are not
// the same string.
export const ASSIGNMENT_MODE_LABELS = {
  round_robin: 'Round robin',
  skill_based: 'Skill based',
};

export const AVAILABILITY_LABELS = {
  on_duty: 'On duty',
  off_duty: 'Off duty',
  vacation: 'Vacation',
  big_project: 'Big project',
};

export const AVAILABILITY_OPTIONS = [
  { value: 'on_duty', label: 'On duty' },
  { value: 'off_duty', label: 'Off duty' },
  { value: 'vacation', label: 'Vacation' },
  { value: 'big_project', label: 'Big project' },
];

/* ── Decision flags ───────────────────────────────────────────────────────
 *
 * The workflow emits flags as SCREAMING_SNAKE constants, sometimes with a
 * `:detail` payload appended (`FORWARD_ONLY_DEPARTMENT:Mobility`). Those are
 * the pipeline's own vocabulary; the person reading the decision drawer is a
 * dispatch supervisor deciding whether the routing was right, and
 * `THREAD_REGEX_UNCONFIRMED` tells them nothing they can act on.
 *
 * This glossary is the single place that turns a flag into a sentence and
 * says how loudly it should read. Presentation only — nothing here changes
 * what the workflow emits or what BOSS stores.
 *
 * Severity tiers, in the order a reviewer should read them:
 *   action — a person has to do something before this ticket moves.
 *   gap    — the AI decided without information it should have had, so the
 *            answer itself may be wrong. No specific chore attached.
 *   info   — pipeline diagnostic. Recorded for the trail; implies nothing
 *            about whether the routing was correct.
 */

export const FLAG_SEVERITY_ORDER = { action: 0, gap: 1, info: 2 };

// Unknown flags land here: loud enough not to be ignored, not so loud that a
// new diagnostic flag from a workflow change looks like an incident.
const DEFAULT_FLAG_SEVERITY = 'gap';

/**
 * `text` is either a string or a function of the flag's `:detail` payload, so
 * a flag that names a department or a file can say which one. `detail` picks
 * that payload out of the colon-separated parts (defaulting to everything
 * after the first colon).
 */
export const FLAG_GLOSSARY = {
  DISTRO_VIA_ORIGINAL_TO: {
    severity: 'info',
    text: "Mailbox identified from the forwarded email's original To: line",
  },
  DISTRO_UNKNOWN: {
    severity: 'gap',
    text: 'Could not tell which mailbox this was sent to',
  },
  ADVISOR_MAP_NOT_SEEDED: {
    severity: 'gap',
    text: 'Advisor list not loaded, so the sender could not be matched to an advisor',
  },
  ADVISOR_UNMATCHED: {
    severity: 'gap',
    text: "Sender's domain is not on the advisor list",
  },
  THREAD_REGEX_UNCONFIRMED: {
    severity: 'info',
    text: 'Could not confirm whether this is part of an existing ticket thread',
  },
  POSSIBLE_DUPLICATE: {
    severity: 'info',
    text: 'May duplicate a ticket already in the queue',
  },
  FORWARD_ONLY_DEPARTMENT: {
    severity: 'action',
    text: (dept) =>
      dept
        ? `Handled by forwarding to the ${dept} mailbox, not by assigning a ticket. Held for a person to forward.`
        : "Handled by forwarding to that team's mailbox, not by assigning a ticket. Held for a person to forward.",
  },
  NON_ROUTABLE_DEPARTMENT: {
    severity: 'gap',
    text: (dept) =>
      dept
        ? `The AI chose ${dept}, a department Dispatch does not route to. Sent to Orders instead.`
        : 'The AI chose a department Dispatch does not route to. Sent to Orders instead.',
  },
  // Separate from the flag above on purpose. That one means the AI named a
  // product category instead of a department, and Orders is the right answer
  // (Chris, 2026-07-30 @33:35, on the Zoom order). This one means the AI named
  // a department that genuinely exists and genuinely is not Dispatch's —
  // "commissions creates their own tickets so they essentially don't go through
  // Dispatch" (@26:11). Those need different handling, so they read differently.
  NOT_DISPATCH_DEPARTMENT: {
    severity: 'action',
    text: (dept) =>
      dept
        ? `This looks like work for ${dept}, which raises its own tickets outside Dispatch. Parked in Orders — check whether it belongs here at all.`
        : 'This looks like work for a team that raises its own tickets outside Dispatch. Parked in Orders — check whether it belongs here at all.',
  },
  PROVIDER_LANE_CONFLICT: {
    severity: 'action',
    // The workflow emits the detail as `body_names=X;attachment=Y` or
    // `body=X;attachment=Y`. Rendering that verbatim is exactly the kind of
    // machine string this glossary exists to keep off the screen.
    text: (detail) => {
      const base =
        'The email and the attachment point at different providers, and nobody has said which one decides the department. Held for you.';
      if (!detail) return base;
      const body = /(?:body_names|body)=([^;]+)/.exec(detail);
      const att = /attachment=([^;]+)/.exec(detail);
      if (!body || !att) return base;
      const named = body[1].trim();
      const lane = att[1].trim();
      const bodySide =
        named === 'none' ? 'The email names no provider' : `The email names ${named}`;
      return `${bodySide}, but the attachment names a ${lane} provider. Nobody has said which one decides the department, so this is held for you.`;
    },
  },
  SPECIALIZED_WITHOUT_NAMED_PROVIDER: {
    severity: 'gap',
    text: 'No AT&T, ACC, Lumen, Verizon or Bell Canada named, so this cannot be a Specialized Teams ticket',
  },
  NO_AVAILABLE_OWNER: {
    severity: 'action',
    text: 'Nobody on this team matches the request',
  },
  NO_TEAM_FOR_DEPARTMENT: {
    severity: 'action',
    text: 'No team is set up for this department',
  },
  ATTACHMENT_UNAVAILABLE: {
    severity: 'action',
    // Emitted as `ATTACHMENT_UNAVAILABLE:<status>:<filename>` — two colons,
    // not one, and the filename is the half a reviewer can act on. Taking
    // parts[1] here would surface the internal status token instead, which is
    // the exact kind of string this glossary exists to keep off the screen.
    detail: (parts) => parts.slice(2).join(':'),
    text: (file) =>
      file ? `Could not read an attachment (${file})` : 'Could not read an attachment',
  },
  ATTACHMENT_IMAGES_TOO_SMALL: {
    severity: 'info',
    text: 'Some images were too small to read, usually signature logos',
  },
  // The four below were found by auditing every distinct flag actually present
  // in dispatcher_decisions (2026-09-08, 143 rows) rather than by reading the
  // workflow. They were reaching the drawer as raw constants, which is the
  // thing this glossary exists to prevent.
  ATTACHMENT_IMAGES_SIZE_FILTERED: {
    // Emitted as `ATTACHMENT_IMAGES_SIZE_FILTERED:<count>`.
    severity: 'gap',
    text: (count) =>
      count
        ? `${count} image${count === '1' ? ' was' : 's were'} skipped for being below the size floor`
        : 'Some images were skipped for being below the size floor',
  },
  ATTACHMENT_IMAGE_VISION_UNSUPPORTED: {
    severity: 'gap',
    text: 'An image was in a format the AI cannot look at',
  },
  UNDEFINED_DEPARTMENT_BUCKET: {
    // The AI named a department outside the known list. Distinct from
    // NON_ROUTABLE_DEPARTMENT, which is a known department Dispatch does not
    // route to; this one is a value nothing recognises at all.
    severity: 'gap',
    text: (dept) =>
      dept
        ? `The AI answered "${dept}", which is not a department we recognise`
        : 'The AI named a department we do not recognise',
  },
  UNDEFINED_TICKET_TYPE_BUCKET: {
    severity: 'gap',
    text: (type) =>
      type
        ? `The AI answered "${type}", which is not a ticket type we recognise`
        : 'The AI named a ticket type we do not recognise',
  },
  INTERNAL_FORWARD: {
    severity: 'info',
    text: 'Forwarded from inside AppDirect',
  },
  SLA_WITHHELD_NO_DEPARTMENT: {
    severity: 'gap',
    text: 'No response clock started, because no department was chosen',
  },
  MULTI_REQUEST_NEEDS_SPLIT: {
    severity: 'action',
    text: 'Contains more than one request and needs splitting by a person',
  },
  CONTRACT_VERSION_AHEAD: {
    severity: 'gap',
    text: 'The email data is newer than this workflow expects',
  },
  TEAMS_LOAD_FAILED: {
    severity: 'gap',
    text: 'Could not load team rosters',
  },
};

/**
 * Flags whose detail is spliced into the NAME with underscores rather than
 * appended after a colon, so they cannot be resolved by exact key.
 *
 * Kept as a short list on purpose: every other flag observed in live decision
 * records uses the `NAME:detail` form, and a new entry here should have to
 * justify itself against a real example rather than being added speculatively.
 */
export const FLAG_NAME_PREFIXES = [
  {
    prefix: 'ATTACHMENT_DOCX_EXTRACT_FAIL_',
    severity: 'action',
    text: (file, reason) =>
      `Could not read the Word document${file ? ` (${file})` : ''}` +
      (reason ? ` — ${reason}` : ''),
  },
];

/**
 * Translate one raw flag string into something a dispatch supervisor can read.
 *
 * Returns `null` for an empty/absent flag so callers can map-and-filter.
 * Otherwise: `{ raw, name, detail, text, severity, known }`.
 *
 * An unrecognised flag keeps its raw string as the display text rather than
 * being dropped. A workflow change that adds a flag we have not written a
 * sentence for should look ugly on screen, not disappear from it — a silently
 * swallowed flag is how a reviewer ends up approving a decision the pipeline
 * had already warned about.
 */
export function describeFlag(flag) {
  if (flag == null) return null;
  const raw = String(flag).trim();
  if (raw === '') return null;

  const parts = raw.split(':');
  const name = parts[0];
  const entry = FLAG_GLOSSARY[name];

  // Not every flag puts its detail after the colon. `ATTACHMENT_DOCX_EXTRACT_FAIL`
  // splices the filename into the NAME with an underscore and puts the parser
  // error after the colon:
  //   ATTACHMENT_DOCX_EXTRACT_FAIL_Nextiva....docx:Module 'zlib' is disallowed
  // Exact-key lookup can never match that, so it fell through to the raw-string
  // fallback and printed the whole constant on screen. Resolved by prefix.
  // Verified against live decision records 2026-09-08: this is the only flag in
  // the collection shaped this way.
  if (!entry) {
    const prefixed = FLAG_NAME_PREFIXES.find((p) => name.startsWith(p.prefix));
    if (prefixed) {
      const file = name.slice(prefixed.prefix.length);
      const reason = parts.slice(1).join(':').trim();
      return {
        raw,
        name: prefixed.prefix.replace(/_$/, ''),
        detail: file,
        text: prefixed.text(file, reason),
        severity: prefixed.severity,
        known: true,
      };
    }
  }

  const detail = entry?.detail
    ? entry.detail(parts)
    : parts.slice(1).join(':');
  const trimmedDetail = detail ? String(detail).trim() : '';

  if (!entry) {
    return {
      raw,
      name,
      detail: trimmedDetail,
      text: raw,
      severity: DEFAULT_FLAG_SEVERITY,
      known: false,
    };
  }

  return {
    raw,
    name,
    detail: trimmedDetail,
    text:
      typeof entry.text === 'function'
        ? entry.text(trimmedDetail)
        : entry.text,
    severity: entry.severity || DEFAULT_FLAG_SEVERITY,
    known: true,
  };
}

/**
 * Describe a whole flag list, ordered by what the reviewer has to act on.
 *
 * Sorting is stable within a tier (Array.prototype.sort is spec-stable since
 * ES2019), so flags keep the workflow's own ordering inside each group.
 */
export function describeFlags(flags) {
  return (Array.isArray(flags) ? flags : [])
    .map(describeFlag)
    .filter(Boolean)
    .sort(
      (a, b) =>
        (FLAG_SEVERITY_ORDER[a.severity] ?? FLAG_SEVERITY_ORDER[DEFAULT_FLAG_SEVERITY]) -
        (FLAG_SEVERITY_ORDER[b.severity] ?? FLAG_SEVERITY_ORDER[DEFAULT_FLAG_SEVERITY]),
    );
}

/**
 * True when a flag means a human is on the hook for something.
 *
 * Was a hand-maintained prefix list; now it reads the glossary's `action`
 * tier so the severity of a flag is defined in exactly one place. The set
 * moved with it: `NO_AVAILABLE_OWNER`, `MULTI_REQUEST_NEEDS_SPLIT` and
 * `FORWARD_ONLY_DEPARTMENT` are chores and now count, while `DISTRO_UNKNOWN`
 * and `ADVISOR_UNMATCHED` describe a decision made without full information —
 * still worth reading, but nobody is being asked to do anything about them.
 *
 * Still matched by base name, so `ATTACHMENT_UNAVAILABLE:timeout:quote.pdf`
 * counts as `ATTACHMENT_UNAVAILABLE`.
 */
export function isCriticalFlag(flag) {
  return describeFlag(flag)?.severity === 'action';
}

/* ── How the decision was reached ─────────────────────────────────────────
 *
 * `dept_source` / `type_source` / `owner_mode` are the workflow's record of
 * which mechanism produced each answer. Printed raw they read as
 * `Dept source: llm`, which tells a supervisor nothing. These translate them
 * into the sentence a person would say out loud. The raw values stay on the
 * screen under Technical details for whoever is debugging the pipeline.
 */

export const DECISION_SOURCE_LABELS = {
  llm: 'the AI read the email',
  llm_low_confidence: 'the AI read the email but was not confident',
  rule: 'matched a written routing rule',
  residual_default: 'no rule matched, so it used the default lane',
  distro_default: 'defaulted from the mailbox it arrived at',
  no_signal: 'nothing in the email to go on',
};

export const OWNER_MODE_METHOD_LABELS = {
  round_robin: "next in the team's rotation",
  skill_based: 'best skill match on the team',
  none: 'not assigned',
};

/**
 * Describe the advisor in a sentence a dispatcher can act on.
 *
 * Two different facts have to stay visibly distinct here. `advisor_match` is a
 * resolved AppDirect advisor, looked up from the sender's domain against the
 * advisor map. `advisor_stated` is whatever name the email itself gives, read
 * out of the text — useful, but unverified. Showing them as one field would
 * let a name somebody typed in a signature block read as a confirmed account.
 *
 * `UNMATCHED` used to render raw, which is exactly the kind of variable-name
 * leakage that made this panel unreadable for a non-technical reviewer.
 *
 * Returns `{ text, tone }` where tone is 'matched' | 'stated' | 'none'.
 */
export function describeAdvisor(decision) {
  const d = decision || {};
  const stated = (d.advisor_stated || '').trim();
  const match = (d.advisor_match || '').trim();
  const matched = match && match !== 'UNMATCHED';

  if (matched) {
    // Stored as "Company:appdirect_id" — show the company, keep the id as
    // supporting detail rather than making a dispatcher decode a colon.
    const [company, id] = match.split(':');
    const label = id ? `${company} (AppDirect ID ${id})` : company;
    return stated && comparisonKey(stated) !== comparisonKey(company)
      ? { text: `${label} — email names ${stated}`, tone: 'matched' }
      : { text: label, tone: 'matched' };
  }
  if (stated) {
    return {
      text: `${stated} — named in the email, no match in the advisor list`,
      tone: 'stated',
    };
  }
  return { text: 'Not identified', tone: 'none' };
}

/**
 * One plain sentence describing how this decision was reached, or `null` when
 * none of the three fields is a value we can translate (so the caller can drop
 * the block rather than render "unknown" three times).
 *
 * Department and ticket type are merged when they came from the same
 * mechanism, which is the common case — repeating the identical clause twice
 * is half of what made the original panel unreadable.
 */
export function decisionMethodSentence(decision) {
  const d = decision || {};
  const dept = DECISION_SOURCE_LABELS[d.dept_source] || null;
  const type = DECISION_SOURCE_LABELS[d.type_source] || null;
  const owner = OWNER_MODE_METHOD_LABELS[d.owner_mode] || null;

  const parts = [];
  if (dept && type && d.dept_source === d.type_source) {
    parts.push(`Department and ticket type: ${dept}.`);
  } else {
    if (dept) parts.push(`Department: ${dept}.`);
    if (type) parts.push(`Ticket type: ${type}.`);
  }
  if (owner) parts.push(`Owner: ${owner}.`);

  return parts.length ? parts.join(' ') : null;
}

/**
 * What to show for `escalation_reason`, or `null` when it adds nothing.
 *
 * The workflow sets it to `flags.join(', ')` and only falls back to
 * `low_confidence:<score>` when there are no flags — so on a flagged decision
 * it is the flag list a second time, in raw constant form. That duplication is
 * the single worst thing on the current screen: the same five constants,
 * printed twice, under two different headings.
 *
 * Returns null when every comma-separated piece is already in `flags` (which
 * covers the exact join and any reordering of it), a readable sentence for the
 * low-confidence form, and the raw string for anything else — an unrecognised
 * escalation reason is a real signal and must not be swallowed.
 */
export function escalationNote(escalationReason, flags) {
  if (escalationReason == null) return null;
  const raw = String(escalationReason).trim();
  if (raw === '') return null;

  const flagSet = new Set((Array.isArray(flags) ? flags : []).map((f) => String(f).trim()));
  const pieces = raw.split(',').map((p) => p.trim()).filter(Boolean);
  if (pieces.length > 0 && pieces.every((p) => flagSet.has(p))) return null;

  const lowConfidence = /^low_confidence:(.*)$/i.exec(raw);
  if (lowConfidence) {
    // `Number('')` is 0, not NaN — without the emptiness check a decision that
    // recorded no score at all would claim "Confidence (0%)", inventing a
    // number on the one screen whose whole job is to stop doing that.
    const rawScore = lowConfidence[1].trim();
    const score = rawScore === '' ? NaN : Number(rawScore);
    return Number.isFinite(score)
      ? `Confidence (${Math.round(score * 100)}%) was below the bar for routing on its own, so it was held for review.`
      : 'The AI was not confident enough to route this on its own, so it was held for review.';
  }

  return raw;
}
